import React, { useContext, useEffect, useState } from 'react';
import { DecisionTreeAnswer, DecisionTreeQuestion } from '../../../../@types';
import { DecisionTreeContext } from '../context/decision-tree-context';
import { assetPrefix } from '../../../../utils';
import {
  Button,
  Highlight,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger
} from '@chakra-ui/react';
import { SharedIconCheck, SharedIconInfo } from '../../../shared/icons';
import ArrowQuestionExplanation from '../icons/arrow-question-explanation';

interface DecisionTreeCurrentQuestionProps {
  question: DecisionTreeQuestion;
}

export const DecisionTreeCurrentQuestion: React.FC<DecisionTreeCurrentQuestionProps> = ({
  question
}: {
  question: DecisionTreeQuestion;
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<DecisionTreeAnswer>();
  const { setAnswer, isLoading, questions, appType } = useContext(DecisionTreeContext);
  const [refExplain, setRefExplain] = useState<HTMLDivElement | null>(null);
  const [refImage, setRefImage] = useState<HTMLDivElement | null>(null);
  const [refQuestion, setRefQuestion] = useState<HTMLDivElement | null>(null);

  const handleCustomEvent = (event: Event) => {
    const customEvent = event as CustomEvent; // Cast Event to CustomEvent
    if (question.id === 1) {
      const answer = question.answers.find(
        (answer) => answer.id === parseInt(customEvent.detail.answer)
      );
      if (answer) {
        handleAnswer(question, answer);
      }
    }
  };

  useEffect(() => {
    const event = `decisionTree${appType.charAt(0).toUpperCase() + appType.slice(1)}`;
    window.addEventListener(event, handleCustomEvent);
    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener(event, handleCustomEvent);
    };
  }, []);

  useEffect(() => {
    if (refExplain && refQuestion && refImage) {
      //console.log(refQuestion, refExplain, refImage, refImage.clientHeight);
      refExplain.style.left = `${
        refQuestion.offsetLeft - refExplain.clientWidth + refExplain.clientWidth / 3
      }px`;
      refExplain.style.top = `${refImage.clientHeight || 260 - refExplain.clientHeight / 2}px`;
    }
  }, [refExplain, refQuestion, questions]);

  const handleAnswer = (question: DecisionTreeQuestion, answer: DecisionTreeAnswer) => {
    setSelectedAnswer(answer);
    setTimeout(() => {
      setSelectedAnswer(undefined);
      setAnswer(question, answer);
    }, 200);
  };

  return (
    <>
      <div ref={setRefExplain} className={`hidden md:block md:absolute`}>
        {question?.id === 1 && <ArrowQuestionExplanation />}
      </div>
      <div className={`flex flex-col md:w-3/5`}>
        <img
          ref={setRefImage}
          className={`mb-4 self-center md:mb-8 w-full max-w-[380px]`}
          alt={question?.img.alt}
          src={`${assetPrefix}${question?.img.src}`}
        />
        <div className={`flex justify-center`}>
          <h3 ref={setRefQuestion} className={`leading-20 md:h-[60px] text-center`}>
            <Highlight
              query={question?.highlight?.words || []}
              styles={{ px: '2', pb: '1', rounded: 'md', bg: '#CBD8E3' }}>
              {question?.question}
            </Highlight>
          </h3>

          {question?.highlight && (
            <div className={`ml-2 md:ml-4`}>
              <Popover placement={`right`}>
                <PopoverTrigger>
                  <SharedIconInfo color={`blue.500`} boxSize={8} />
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverCloseButton />
                  <PopoverHeader>
                    <span className={`first-letter:uppercase`}>{question.highlight?.words[0]}</span>
                  </PopoverHeader>
                  <PopoverBody>{question.highlight?.explanation}</PopoverBody>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
        <div className={`flex flex-wrap gap-4 justify-center mt-8`}>
          {question?.answers &&
            question?.answers.map((answer, key) => (
              <div className={`min-w-[100px]`}>
                <Button
                  className={`w-full`}
                  key={key}
                  colorScheme={selectedAnswer?.id === answer.id ? `green` : undefined}
                  variant={selectedAnswer?.id === answer.id ? `solid` : `whiteToGreen`}
                  leftIcon={selectedAnswer?.id === answer.id ? <SharedIconCheck /> : undefined}
                  onClick={() => handleAnswer(question, answer)}
                  isLoading={isLoading}>
                  {answer.answer}
                </Button>
              </div>
            ))}
        </div>
      </div>
    </>
  );
};
